import {
  Box,
  Button,
  Flex,
  Image,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import colors from "../../../app/colors";
import {
  type DesktopCaptureSource,
  getDesktopCaptureSourceKind,
  getDesktopCaptureSources,
} from "./desktop-screen-share";

interface VideoCallScreenSharePickerProps {
  onSelect: (source: DesktopCaptureSource) => void;
  onCancel: () => void;
}

const SourceTile: React.FC<{
  source: DesktopCaptureSource;
  onSelect: (source: DesktopCaptureSource) => void;
}> = ({ source, onSelect }) => (
  <VStack
    as="button"
    type="button"
    spacing="8px"
    align="stretch"
    p="8px"
    borderRadius="12px"
    border="2px solid transparent"
    bg="rgba(255, 255, 255, 0.04)"
    cursor="pointer"
    textAlign="left"
    _hover={{ bg: "rgba(255, 255, 255, 0.1)", borderColor: colors.blue }}
    _focusVisible={{ borderColor: colors.blue, outline: "none" }}
    onClick={() => onSelect(source)}
  >
    <Box
      h="110px"
      borderRadius="8px"
      bg={colors.black}
      overflow="hidden"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Image
        src={source.thumbnail}
        alt=""
        maxW="100%"
        maxH="100%"
        objectFit="contain"
      />
    </Box>
    <Flex align="center" gap="6px" minW={0}>
      {source.appIcon && (
        <Image src={source.appIcon} alt="" boxSize="16px" flexShrink={0} />
      )}
      <Text
        fontSize="13px"
        fontWeight={600}
        color={colors.white}
        noOfLines={1}
        title={source.name}
      >
        {source.name}
      </Text>
    </Flex>
  </VStack>
);

const SourceSection: React.FC<{
  title: string;
  sources: DesktopCaptureSource[];
  onSelect: (source: DesktopCaptureSource) => void;
}> = ({ title, sources, onSelect }) => {
  if (sources.length === 0) return null;
  return (
    <VStack align="stretch" spacing="10px">
      <Text
        fontSize="12px"
        fontWeight={700}
        textTransform="uppercase"
        letterSpacing="0.04em"
        color={colors.gray30}
      >
        {title}
      </Text>
      <SimpleGrid columns={{ base: 2, md: 3 }} spacing="10px">
        {sources.map((source) => (
          <SourceTile key={source.id} source={source} onSelect={onSelect} />
        ))}
      </SimpleGrid>
    </VStack>
  );
};

/**
 * Desktop-app replacement for the browser's native screen picker. Lists every screen and
 * window reported by Electron and hands the chosen one back via `onSelect`.
 */
export const VideoCallScreenSharePicker: React.FC<
  VideoCallScreenSharePickerProps
> = ({ onSelect, onCancel }) => {
  const [sources, setSources] = useState<DesktopCaptureSource[] | undefined>();
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getDesktopCaptureSources()
      .then((result) => {
        if (!cancelled) setSources(result);
      })
      .catch((error: unknown) => {
        // eslint-disable-next-line no-console
        console.error("Unable to list screen share sources", error);
        if (!cancelled) setHasError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  const { screens, windows } = useMemo(() => {
    const all = sources ?? [];
    return {
      screens: all.filter((s) => getDesktopCaptureSourceKind(s) === "screen"),
      windows: all.filter((s) => getDesktopCaptureSourceKind(s) === "window"),
    };
  }, [sources]);

  let body: React.ReactNode;
  if (hasError) {
    body = (
      <Text fontSize="14px" color={colors.gray30}>
        Unable to list your screens and windows. Please try again.
      </Text>
    );
  } else if (!sources) {
    body = (
      <Flex justify="center" py="32px">
        <Spinner color={colors.white} />
      </Flex>
    );
  } else if (sources.length === 0) {
    body = (
      <Text fontSize="14px" color={colors.gray30}>
        Nothing available to share.
      </Text>
    );
  } else {
    body = (
      <VStack align="stretch" spacing="20px">
        <SourceSection title="Screens" sources={screens} onSelect={onSelect} />
        <SourceSection title="Windows" sources={windows} onSelect={onSelect} />
      </VStack>
    );
  }

  return (
    <Flex
      role="dialog"
      aria-modal
      aria-label="Share your screen"
      position="fixed"
      inset={0}
      zIndex={10}
      align="center"
      justify="center"
      bg="rgba(0, 0, 0, 0.6)"
      onClick={onCancel}
    >
      <Flex
        direction="column"
        w="min(720px, calc(100% - 32px))"
        maxH="calc(100% - 64px)"
        bg="rgba(28, 28, 28, 0.98)"
        border="1px solid rgba(255, 255, 255, 0.08)"
        borderRadius="16px"
        boxShadow="0 16px 48px rgba(0, 0, 0, 0.5)"
        overflow="hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <Text
          px="20px"
          pt="20px"
          pb="12px"
          fontSize="18px"
          fontWeight={700}
          color={colors.white}
        >
          Share your screen
        </Text>
        <Box px="20px" pb="12px" overflowY="auto" flex="1" minH={0}>
          {body}
        </Box>
        <Flex justify="flex-end" px="20px" py="14px">
          <Button
            variant="ghost"
            color={colors.white}
            _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
            onClick={onCancel}
          >
            Cancel
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
